import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { StatusCircle, PressureGauge, VerticalGauge, TankGraphic } from "../components/acms/Indicators";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { Zap, Radio, Activity, Wind, Siren, Lightbulb } from "lucide-react";

function Card({ title, subtitle, children, accent, testid }) {
  return (
    <div className="glass p-5 rounded-lg" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-600">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {accent && <span className="text-[10px] uppercase tracking-widest f360-gradient-text font-bold">{accent}</span>}
      </div>
      {children}
    </div>
  );
}

function PowerToggle({ label, on }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
        {on ? "ON" : "OFF"}
      </span>
    </div>
  );
}

export default function ACMSLiveMonitor() {
  const { selectedSite, sites } = useSiteContext();
  const [tele, setTele] = useState(null);
  const [hist, setHist] = useState([]);
  const [siteCfg, setSiteCfg] = useState(null);

  useEffect(() => {
    if (!selectedSite || selectedSite === "all") return;
    const load = async () => {
      // Try IPC telemetry endpoint first (§7b of frontend guide)
      try {
        const r = await api.get(`/v1/sites/${selectedSite}/telemetry/snapshot`);
        // r.data is an array of { parameter, value, ... } — reshape into the flat object the render code expects
        if (Array.isArray(r.data) && r.data.length > 0) {
          const flat = {};
          r.data.forEach((row) => { flat[row.parameter] = row.value; });
          // Translate IPC register names to legacy telemetry field names expected by the render layer
          const freshAt = r.data[0].ingested_at || r.data[0].timestamp || new Date().toISOString();
          const mapped = {
            // Preserve all fields from previous snapshot so the rest of the UI doesn't blank out
            ...(tele || {}),
            // Override with fresh IPC values
            updated_at: freshAt,
            main_hydrant_pump: flat["main-pump-di-1"] === 1 ? "running" : flat["main-pump-di-1"] === 0 ? "stopped" : "unknown",
            hyd_jockey_pump: flat["hyd-joc-di-2"] === 1 ? "running" : "stopped",
            main_sprinkler_pump: flat["spr-pump-di-3"] === 1 ? "running" : "stopped",
            sprinkler_jockey_pump: flat["spr-joc-di-4"] === 1 ? "running" : "stopped",
            hydrant_pressure_bar: flat["ch123"] ?? tele?.hydrant_pressure_bar ?? 0,
            sprinkler_pressure_bar: flat["ch124"] ?? tele?.sprinkler_pressure_bar ?? 0,
          };
          setTele(mapped);
          return;
        }
      } catch { /* fall through to legacy endpoint */ }
      // Fallback: old generic telemetry endpoint
      api.get(`/telemetry/${selectedSite}`).then((r) => setTele(r.data)).catch(() => {});
    };
    const loadHistory = () => {
      // Try IPC parameter-history for hydrant pressure trend
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, { params: { parameter: "ch123", hours: 24 } })
        .then((r) => {
          if (Array.isArray(r.data) && r.data.length > 0) {
            setHist(r.data.map((x) => ({
              ...x,
              hydrant_pressure_bar: x.value,
              time: new Date(x.timestamp || x.ingested_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
            })));
          }
        })
        .catch(() => {
          // Fallback to legacy history endpoint
          api.get(`/telemetry/${selectedSite}/history`, { params: { hours: 24 } }).then((r) => {
            setHist(r.data.map((x) => ({ ...x, time: new Date(x.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) })));
          }).catch(() => {});
        });
    };
    load();
    loadHistory();
    api.get(`/site-config-full/${selectedSite}`).then((r) => setSiteCfg(r.data)).catch(() => {});
    const i = setInterval(() => { load(); loadHistory(); }, 5000);
    return () => clearInterval(i);
  }, [selectedSite]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentSite = sites.find((s) => s.site_id === selectedSite);

  if (!tele) return <div className="text-gray-500">Connecting to gateway…</div>;

  const heightM = Number(siteCfg?.building?.height_m || currentSite?.total_height_m || 0);
  const btCount = Number(siteCfg?.break_tank_count ?? (heightM >= 90 ? 1 : 0));
  const seedN = (selectedSite || "").charCodeAt(0) || 0;
  const breakTanksLive = Array.from({ length: btCount }).map((_, i) => ({
    floor: (siteCfg?.break_tank_floors || [])[i] || `Refuge ${18 + i * 13}`,
    level: Math.max(20, Math.min(95, 65 + ((seedN + i * 7) % 30) - 10)),
    duty: true,
    standby: false,
    power: true,
  }));

  return (
    <div className="space-y-6" data-testid="acms-live-monitor">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-orange-400">ACMS Live Monitor</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{currentSite?.name}</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time IoT telemetry · GR mandated parameters · auto-refresh 5s</p>
        </div>
        <div className="glass px-4 py-2 rounded-lg text-xs text-gray-500">
          Last data: <span className="text-emerald-400 font-semibold">{new Date(tele.updated_at).toLocaleString("en-IN", { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
        </div>
      </div>

      {/* SECTION A — HYDRANT */}
      <section>
        <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-orange-400" strokeWidth={2.5} /> Section A · Hydrant System <span className="text-[10px] text-slate-600">Appendix b</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card title="Main Hydrant Pump" subtitle="Differential Pressure Switch" testid="card-main-hydrant-pump">
            <StatusCircle status={tele.main_hydrant_pump} label="Main Hydrant" subtitle={tele.main_hydrant_pump} />
          </Card>
          <Card title="Standby Diesel Pump" subtitle="Auto-cutover on main failure" testid="card-standby-pump">
            <StatusCircle status={tele.standby_pump} label="Standby" subtitle={tele.standby_pump} />
          </Card>
          <Card title="Diesel Tank Level" subtitle="Alert below 20%" testid="card-diesel">
            <div className="flex justify-center"><VerticalGauge value={tele.diesel_tank_pct} label="Diesel" thresholds={{ low: 25, mid: 50 }} /></div>
          </Card>
          <Card title="Jockey Pump" subtitle={`${tele.jockey_cycles_per_hr} cycles / hr`} testid="card-jockey">
            <StatusCircle status={tele.jockey_pump} label="Jockey" subtitle={tele.jockey_pump} />
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <Card title="Hydrant Riser Pressure" subtitle="GR Minimum 3.5 Bar @ terrace" accent="CRITICAL" testid="card-hydrant-pressure">
            <PressureGauge value={tele.hydrant_pressure_bar} max={10} threshold={3.5} label="Bar" critical />
          </Card>
          <Card title="24h Pressure Trend" subtitle="Hydrant riser" testid="card-hydrant-trend">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={hist}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="time" hide />
                <YAxis domain={[0, 10]} stroke="#64748b" fontSize={10} />
                <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
                <ReferenceLine y={3.5} stroke="#ff3366" strokeDasharray="4 4" label={{ value: "3.5 Bar GR", position: "right", fill: "#ff3366", fontSize: 10 }} />
                <Line type="monotone" dataKey="hydrant_pressure_bar" stroke="#ff6b35" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          <Card title="Power Status" subtitle="Voltage sensors at MCC" testid="card-hydrant-power">
            <div className="space-y-2">
              <PowerToggle label="Main Hydrant Pump Power" on={tele.power_main_hydrant} />
              <PowerToggle label="Jockey Pump Power" on={tele.power_jockey} />
              <PowerToggle label="Booster Pump Power" on={tele.power_booster} />
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <span className="text-sm text-gray-600">Booster Pump Status</span>
                <span className={`text-xs font-bold uppercase ${tele.booster_pump === "running" ? "text-emerald-400" : "text-gray-500"}`}>{tele.booster_pump}</span>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* SECTION B — FIRE WATER TANK */}
      <section>
        <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-pink-400" strokeWidth={2.5} /> Section B · Fire Water Tanks <span className="text-[10px] text-slate-600">Appendix c</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Underground Fire Tank" subtitle="Ultrasonic level sensor · alert < 30%" testid="card-ug-tank">
            <TankGraphic value={tele.ug_tank_pct} label="UG Tank" liters={tele.ug_tank_liters} />
          </Card>
          <Card title="Overhead Tank" subtitle="Alert < 25%" testid="card-oht">
            <TankGraphic value={tele.oht_tank_pct} label="Overhead" liters={tele.oht_tank_liters} />
          </Card>
        </div>
      </section>

      {/* SECTION B2 — BREAK TANKS & INTERMEDIATE PUMPS (only if site has break tanks) */}
      {breakTanksLive.length > 0 && (
        <section data-testid="acms-section-b2">
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-orange-400" strokeWidth={2.5} /> Section B2 · Break Tanks & Intermediate Pumps <span className="text-[10px] text-slate-600">DCPR 2034 · High-Rise Mandatory</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {breakTanksLive.map((bt, i) => (
              <Card key={i} title={`Break Tank ${i + 1} · ${bt.floor}`} subtitle="Alert < 25%" accent={bt.level < 25 ? "CRITICAL" : null} testid={`acms-card-bt-${i}`}>
                <TankGraphic value={bt.level} label={`BT-${i + 1}`} liters={Math.round(bt.level * 250)} />
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px]">Duty Pump</div><div className={`font-bold ${bt.duty ? "text-emerald-400" : "text-gray-500"}`}>{bt.duty ? "RUNNING" : "STOPPED"}</div></div>
                  <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px]">Standby</div><div className={`font-bold ${bt.standby ? "text-emerald-400" : "text-gray-500"}`}>{bt.standby ? "RUNNING" : "STANDBY"}</div></div>
                  <div className="bg-gray-50 rounded p-2 col-span-2"><PowerToggle label="MCC Power" on={bt.power} /></div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* SECTION C — SPRINKLER */}
      <section>
        <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-sky-400" strokeWidth={2.5} /> Section C · Sprinkler System <span className="text-[10px] text-slate-600">Appendix d</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Main Sprinkler Pump" subtitle="Differential pressure switch" testid="card-sprinkler-pump">
            <StatusCircle status={tele.main_sprinkler_pump} label="Main Sprinkler" subtitle={tele.main_sprinkler_pump} />
            <div className="mt-4"><PowerToggle label="Power to Pump (MCC)" on={tele.power_main_sprinkler} /></div>
          </Card>
          <Card title="Sprinkler Riser Pressure" subtitle="GR Hardcoded Minimum 5 Bar" accent="CRITICAL" testid="card-sprinkler-pressure">
            <PressureGauge value={tele.sprinkler_pressure_bar} max={10} threshold={5} label="Bar" critical />
          </Card>
          <Card title="24h Sprinkler Trend" subtitle="Threshold: 5 Bar" testid="card-sprinkler-trend">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={hist}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="time" hide />
                <YAxis domain={[0, 10]} stroke="#64748b" fontSize={10} />
                <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
                <ReferenceLine y={5} stroke="#ff3366" strokeDasharray="4 4" label={{ value: "5 Bar GR", position: "right", fill: "#ff3366", fontSize: 10 }} />
                <Line type="monotone" dataKey="sprinkler_pressure_bar" stroke="#4fd1ff" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </section>

      {/* SECTION D — DETECTION */}
      <section>
        <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
          <Radio className="w-4 h-4 text-emerald-400" strokeWidth={2.5} /> Section D · Detection System <span className="text-[10px] text-slate-600">Appendix e</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: tele.detection_panels_total }).map((_, i) => (
            <Card key={i} title={`Fire Panel ${i + 1}`} subtitle={`Zone ${i + 1} · POTS free contacts`} testid={`card-panel-${i}`}>
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded-full bg-emerald-500 status-pulse-green" />
                <div>
                  <div className="text-sm font-bold text-gray-900">Active</div>
                  <div className="text-[10px] text-gray-500">Last comm: just now</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* SECTION E — VENTILATION */}
      <section>
        <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
          <Wind className="w-4 h-4 text-indigo-400" strokeWidth={2.5} /> Section E · Ventilation & Safety
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card title="Staircase Pressurization" testid="card-vent-1"><StatusCircle status={tele.staircase_pressurization_fan} label="Fan" subtitle={tele.staircase_pressurization_fan} /></Card>
          <Card title="Smoke Exhaust Fan" testid="card-vent-2"><StatusCircle status={tele.smoke_exhaust_fan} label="Exhaust" subtitle={tele.smoke_exhaust_fan} /></Card>
          <Card title="Public Address" testid="card-pa"><StatusCircle status={tele.pa_system} label="PA System" subtitle={tele.pa_system} /></Card>
          <Card title="Emergency Lighting" testid="card-emergency"><StatusCircle status={tele.emergency_lighting} label="Lighting" subtitle={tele.emergency_lighting} /></Card>
        </div>
      </section>

      {/* Gateway status */}
      <section className="glass-strong rounded-lg p-5 border border-orange-500/10" data-testid="gateway-status-bar">
        <div className="flex flex-wrap items-center gap-6 text-xs">
          <div><span className="text-gray-500">Gateway:</span> <span className="font-mono text-orange-400">GW-{selectedSite?.slice(-6).toUpperCase()}</span></div>
          <div><span className="text-gray-500">Connectivity:</span> <span className="text-gray-900">4G · LTE</span></div>
          <div className="flex items-center gap-2"><span className="text-gray-500">Signal:</span> <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <span key={i} className={`w-1 rounded-sm ${i<=4?"bg-emerald-500":"bg-gray-100"}`} style={{height:`${i*3+4}px`}} />)}</div></div>
          <div><span className="text-gray-500">UPS Battery:</span> <span className="text-emerald-400 font-bold">92%</span></div>
          <div><span className="text-gray-500">Stored Events:</span> <span className="text-gray-900">245 / 10,000</span></div>
          <div><span className="text-gray-500">MQTTS:</span> <span className="text-emerald-400 font-bold">● Connected</span></div>
          <div className="ml-auto"><span className="text-gray-500">Last Data:</span> <span className="text-gray-900">{new Date(tele.updated_at).toLocaleTimeString("en-IN")}</span></div>
        </div>
      </section>
    </div>
  );
}
